import { useModal } from "@ebay/nice-modal-react";
import { useForm } from "react-hook-form";

import CodeDiff from "components/CodeDiff";
import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Textarea from "ds/components/Textarea";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import useIntentResourceOperationReview from "shared/IntentProject/useIntentResourceOperationReview";
import { IntentResourceOperation } from "types/generated";

type IntentProjectReviewDrawerProps = {
  operation: IntentResourceOperation;
  intentProjectId: string;
};

type IntentProjectReviewFormFields = {
  note: string;
};

const IntentProjectReviewDrawer = createDrawer(
  ({ operation, intentProjectId }: IntentProjectReviewDrawerProps) => {
    const drawer = useModal();
    const { reportSuccess } = useTypedContext(FlashContext);

    const { canReview, approve, reject, isApproving, isRejecting, actionsDisabled } =
      useIntentResourceOperationReview(operation, intentProjectId);

    const { register, handleSubmit, formState } = useForm<IntentProjectReviewFormFields>({
      defaultValues: { note: "" },
    });

    const handleClose = () => {
      drawer.hide();
    };

    const onReviewed = () => {
      reportSuccess({ message: "Review submitted." });
      drawer.hide();
    };

    const submitApprove = handleSubmit(({ note }) => approve(note, onReviewed));
    const submitReject = handleSubmit(({ note }) => reject(note, onReviewed));

    return (
      <DrawerForm variant="wide" isDirty={formState.isDirty} onClose={handleClose}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Review" />
          <DrawerCloseIcon />
        </DrawerHeader>

        <DrawerBody gap="x-large">
          <CodeDiff
            original={operation.currentState ?? ""}
            modified={operation.proposedState ?? ""}
            title="Diff"
            fullScreenTitle="Operation diff"
          />

          <FormField label="Review note" isOptional noMargin>
            {({ ariaInputProps }) => (
              <Textarea
                placeholder="Enter a note here..."
                disabled={actionsDisabled}
                {...register("note", { setValueAs: (value: string) => value.trim() })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </DrawerBody>

        <DrawerFooter>
          <DrawerFooterActions>
            <Tooltip
              active={!!canReview.message}
              on={(tooltipProps) => (
                <Button
                  {...tooltipProps}
                  variant="secondary"
                  onPress={() => submitReject()}
                  loading={isRejecting}
                  disabled={actionsDisabled}
                >
                  Reject
                </Button>
              )}
            >
              {canReview.message}
            </Tooltip>
            <Tooltip
              active={!!canReview.message}
              on={(tooltipProps) => (
                <Button
                  {...tooltipProps}
                  variant="primary"
                  onPress={() => submitApprove()}
                  loading={isApproving}
                  disabled={actionsDisabled}
                >
                  Approve & Resume
                </Button>
              )}
            >
              {canReview.message}
            </Tooltip>
          </DrawerFooterActions>
        </DrawerFooter>
      </DrawerForm>
    );
  }
);

export const showIntentProjectReviewDrawer = createDrawerTrigger(IntentProjectReviewDrawer);
